import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../../../../../shared/services/utils.service';
import {
	ContractNoticeDetailsCreateRequest,
	ContractNoticeDetailsResponse,
	CreateContractAnnouncementRequest,
	CreateContractNoticeRequest,
	ENabavkiBodyRequest,
	EProkurimiBodyRequest,
	ManagementBusinessEntityControllerService,
	ManagementBusinessEntityResponse,
	ManagementContractAnnouncementControllerService,
	ManagementContractAnnouncementResponse,
	ManagementContractNoticeControllerService,
	ManagementContractNoticeResponse,
	ManagementManualSchedulingControllerService,
	ManagementMediumContractNoticesControllerService,
	ManagementMediumResponse,
	ManagementScreenshotControllerService,
	ManagementTagResponse,
	ManualSchedulingRequest,
	UpdateContractAnnouncementRequest,
	UpdateContractNoticeRequest,
} from 'api-contract-notices';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { take } from 'rxjs/internal/operators/take';
import { Constants } from '../../../../../shared/interfaces/constants';
import { SharedService } from '../../../../../shared/services/shared.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { BusinessEntityComponent } from '../../../management/business-entity/business-entity';
import { MediumComponent } from '../../../management/medium/medium.component';
import { UploadManagementService } from '../../../../../shared/services/upload-management.service';
import { ConfirmationDialogComponent } from '../../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ScreenshotQueueDialogComponent } from '../../../../../shared/components/screenshot-queue-dialog/screenshot-queue-dialog.component';
import { HttpResponse } from '@angular/common/http';

interface ManagementContractAnnouncementWithContractNoticeDetails extends ManagementContractAnnouncementResponse {
	contractAuthority?: ManagementBusinessEntityResponse;
	mediums?: ManagementMediumResponse[];
	details?: ContractNoticeDetailsResponse[];
	number?: string;
	estimatedPurchaseValue?: number;
}

interface ManagementContractNoticeWithParentDetails extends ManagementContractNoticeResponse {
	parentType?: string;
}

@Component({
	selector: 'app-contract-notices-form',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatSelectModule,
		TranslateModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatInputModule,
		MatButtonModule,
		MatIconModule,
		MatCheckboxModule,
		MatCardModule,
		MatAutocompleteModule,
		MatDividerModule,
		MatChipsModule,
		MatTabsModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
	],
	templateUrl: './contract-notices-form.component.html',
	styleUrl: './contract-notices-form.component.scss',
})
export class ContractNoticesFormComponent implements OnInit, OnChanges, OnDestroy {
	private onDestroy: Subject<any> = new Subject();

	@Input() dataAdministrationType: 'CONTRACT_NOTICES' | 'CONTRACT_ANNOUNCEMENTS' = 'CONTRACT_NOTICES';
	@Input() itemForEdit: ManagementContractNoticeWithParentDetails | ManagementContractAnnouncementWithContractNoticeDetails | undefined;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	mode: 'NEW' | 'EDIT' = 'NEW';

	countries = Constants.COUNTRIES.map(c => c?.countryCode);
	contractNoticeFormGroup: FormGroup = new FormGroup({});
	types = Constants.CONTRACT_NOTICE_TYPES;

	contractTypes = ['GOODS', 'SERVICES', 'WORKS'];
	procedureTypes = ['DUMMY1'];

	filteredBusinessEntities: ManagementBusinessEntityResponse[] = [];
	lastUsedContractAuthority: ManagementBusinessEntityResponse | null = null;

	@ViewChild('mediumInput', { static: false }) mediumInput!: ElementRef<HTMLInputElement>;
	filteredMediums: ManagementMediumResponse[] | undefined;
	selectedMediums: ManagementMediumResponse[] = [];

	@ViewChild('contractorInput', { static: false }) contractorInput!: ElementRef<HTMLInputElement>;
	selectedContractors: ManagementBusinessEntityResponse[] = [];

	@ViewChild('contractNoticeInput', { static: false }) contractNoticeInput!: ElementRef<HTMLInputElement>;
	filteredContractNotices: ManagementContractNoticeResponse[] = [];
	selectedContractNotice: ManagementContractNoticeResponse | undefined;

	selectedTabIndex = 0;
	avaliableLanguagesCodes = Constants.AVAILABLE_LANGUAGES.map(item => item.code);

	apiLoading = {
		contractNotice: false,
		translateContractNotices: false,
	};

	constructor(
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiContractNoticesMedium: ManagementMediumContractNoticesControllerService,
		private apiManagementBusinessEntity: ManagementBusinessEntityControllerService,
		private apiManagementContractNotice: ManagementContractNoticeControllerService,
		private apiManagementContractAnnouncement: ManagementContractAnnouncementControllerService,
		private apiManagementManualScheduling: ManagementManualSchedulingControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private dialog: MatDialog,
		private uploadManagement: UploadManagementService,
		private apiScreenshot: ManagementScreenshotControllerService
	) {
		this.formInitializer();

		this.sharedService.requestActionInComponent.pipe(takeUntilDestroyed()).subscribe({
			next: response => {
				if (response.componentName === 'ContractNoticesFormComponent') {
					switch (response.action) {
						case 'RESET_FORM': {
							this.resetForm();
							break;
						}
					}
				}
			},
		});
	}

	ngOnInit(): void {
		this.formControlWatchers();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['dataAdministrationType'] && changes['dataAdministrationType'].currentValue) {
			this.types = changes['dataAdministrationType'].currentValue === 'CONTRACT_NOTICES' ? Constants.CONTRACT_NOTICE_TYPES : Constants.CONTRACT_ANNOUNCEMENT_TYPES;

			if (this.dataAdministrationType === 'CONTRACT_NOTICES') {
				this.contractNoticeFormGroup.controls['deliveryDate'].addValidators([Validators.required]);
			} else {
				this.contractNoticeFormGroup.controls['deliveryDate'].removeValidators([Validators.required]);
			}
		}

		if (changes['itemForEdit'] && changes['itemForEdit'].currentValue) {
			this.contractNoticeFormGroup.reset();
			this.selectedMediums = [];
			this.selectedContractors = [];
			this.selectedContractNotice = undefined;
			this.filteredContractNotices = [];
			this.mode = 'EDIT';
			this.populateForm();
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	/** Form methods */
	private formInitializer() {
		this.contractNoticeFormGroup = this.fb.group({
			publishedDate: [new Date(), Validators.required],
			deliveryDate: [, Validators.required],
			type: [, Validators.required],
			url: [, [Validators.pattern(this.utils.VALIDATION_PATTERNS.get('URL') || '')]],
			mediumId: [],
			contractorIds: [],
			contractNoticeId: [],
			contractAuthorityId: [, Validators.required],
			description: [],
			tagIds: [],
			country: [, Validators.required],

			number: [],
			procurementType: [],
			divisiblePurchase: [false],
			estimatedPurchaseValue: [0],
			procedureType: [],
			agreementValue: [],
			currency: ['MKD'],
			fileUrl: [],
		});

		this.avaliableLanguagesCodes.forEach(code => {
			let componentName = 'id' + this.utils.titleCase(code);
			let control: FormControl = new FormControl();
			this.contractNoticeFormGroup.addControl(componentName, control);

			componentName = 'contractSubject' + this.utils.titleCase(code);
			control = new FormControl();
			this.contractNoticeFormGroup.addControl(componentName, control);

			componentName = 'contractNoticeId' + this.utils.titleCase(code);
			control = new FormControl();
			this.contractNoticeFormGroup.addControl(componentName, control);

			componentName = 'translated' + this.utils.titleCase(code);
			control = new FormControl();
			this.contractNoticeFormGroup.addControl(componentName, control);
		});
	}

	private populateForm() {
		if (this.itemForEdit) {
			this.contractNoticeFormGroup.enable();
			this.contractNoticeFormGroup.patchValue(this.itemForEdit);

			let detailsByLang;
			this.avaliableLanguagesCodes.forEach(code => {
				if (this.itemForEdit) {
					detailsByLang = this.itemForEdit.details?.find(item => item.lang?.toLowerCase() === code);
					if (detailsByLang) {
						this.contractNoticeFormGroup.controls['id' + this.utils.titleCase(code)].setValue(detailsByLang.id);
						this.contractNoticeFormGroup.controls['contractSubject' + this.utils.titleCase(code)].setValue(detailsByLang.contractSubject);
						this.contractNoticeFormGroup.controls['contractNoticeId' + this.utils.titleCase(code)].setValue(detailsByLang.contractNoticeId);
						this.contractNoticeFormGroup.controls['translated' + this.utils.titleCase(code)].setValue(detailsByLang.translated);
					}
				}
			});

			this.selectedMediums = this.itemForEdit.mediums || [];
			this.selectedContractors = this.itemForEdit.contractors || [];
			this.contractNoticeFormGroup.controls['tagIds'].setValue(this.itemForEdit.tags?.map((tag: ManagementTagResponse) => tag.id));
			this.contractNoticeFormGroup.controls['contractAuthorityId'].setValue(this.itemForEdit.contractAuthority);

			if ((this.itemForEdit as ManagementContractAnnouncementWithContractNoticeDetails).contractNotice) {
				this.selectedContractNotice = (this.itemForEdit as ManagementContractAnnouncementWithContractNoticeDetails).contractNotice;
				this.contractNoticeFormGroup.controls['contractNoticeId'].setValue(this.selectedContractNotice);
			}

			if ((this.itemForEdit as ManagementContractNoticeWithParentDetails).parentType === 'MODIFICATION' || (this.itemForEdit as ManagementContractNoticeWithParentDetails).parentType === 'CANCELLATION') {
				this.selectedContractNotice = (this.itemForEdit as ManagementContractNoticeWithParentDetails);
				this.contractNoticeFormGroup.controls['contractNoticeId'].setValue(this.selectedContractNotice);
			}

			this.selectedTabIndex = 0;

			if (this.dataAdministrationType === 'CONTRACT_ANNOUNCEMENTS' || (this.itemForEdit as ManagementContractNoticeWithParentDetails).parentType) {
				this.contractNoticeFormGroup.controls['contractAuthorityId'].disable();
				this.contractNoticeFormGroup.controls['mediumId'].disable();
				this.contractNoticeFormGroup.controls['type'].disable();
				this.avaliableLanguagesCodes.forEach(langCode => {
					this.contractNoticeFormGroup.controls['contractSubject' + this.utils.titleCase(langCode)].disable();
				});
				this.contractNoticeFormGroup.controls['number'].disable();
				this.contractNoticeFormGroup.controls['procurementType'].disable();
				this.contractNoticeFormGroup.controls['procedureType'].disable();

				if ((this.itemForEdit as ManagementContractNoticeWithParentDetails).parentType) {
				}
				this.contractNoticeFormGroup.controls['type'].setValue(
					(this.itemForEdit as ManagementContractNoticeWithParentDetails).parentType
						? (this.itemForEdit as ManagementContractNoticeWithParentDetails).parentType
						: (this.itemForEdit as ManagementContractNoticeWithParentDetails).type
				);
			}
		}
	}

	private formControlWatchers() {
		if (this.contractNoticeFormGroup.controls['contractAuthorityId']) {
			this.contractNoticeFormGroup.controls['contractAuthorityId'].valueChanges
				.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					this.itemAction('SEARCH', 'BUSINESS_ENTITY', searchTerm);
				});
		}

		if (this.contractNoticeFormGroup.controls['mediumId']) {
			this.contractNoticeFormGroup.controls['mediumId'].valueChanges
				.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					this.itemAction('SEARCH', 'MEDIUM', searchTerm);
				});
		}

		if (this.contractNoticeFormGroup.controls['contractorIds']) {
			this.contractNoticeFormGroup.controls['contractorIds'].valueChanges
				.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					this.itemAction('SEARCH', 'BUSINESS_ENTITY', searchTerm);
				});
		}

		if (this.contractNoticeFormGroup.controls['contractNoticeId']) {
			this.contractNoticeFormGroup.controls['contractNoticeId'].valueChanges
				.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					if (typeof searchTerm === 'string') {
						this.itemAction('SEARCH', 'CONTRACT_NOTICE', searchTerm);
					}
				});
		}

		if (this.contractNoticeFormGroup.controls['url']) {
			this.contractNoticeFormGroup.controls['url'].valueChanges
				.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(url => {
					if (url && this.mode === 'NEW' && this.selectedMediums.length === 0) {
						this.autoPopulateMediumFromUrl(url);
					}
				});
		}
	}

	checkFormValidity() {
		let validity = false;

		this.avaliableLanguagesCodes.some(code => {
			return (validity = this.contractNoticeFormGroup.controls['contractSubject' + this.utils.titleCase(code)].value);
		});

		if (validity) {
			validity =
				this.selectedMediums.length &&
				(this.contractNoticeFormGroup.controls['url'].value ||
					this.contractNoticeFormGroup.controls['description'].value ||
					this.contractNoticeFormGroup.controls['fileUrl'].value);
		}
		return validity;
	}

	resetForm(formDirective?: FormGroupDirective) {
		// this.clipForEdit = {
		// 	id: '',
		// 	tags: [],
		// };
		this.contractNoticeFormGroup.reset();
		if (formDirective) {
			formDirective.resetForm();
		}
		this.selectedMediums = [];
		this.selectedContractors = [];
		this.selectedContractNotice = undefined;
		this.filteredContractNotices = [];

		// set default values
		this.contractNoticeFormGroup.controls['publishedDate'].setValue(new Date());
		this.contractNoticeFormGroup.controls['country'].setValue('MK');
		this.contractNoticeFormGroup.controls['currency'].setValue('MKD');

		if (!formDirective) {
			this.lastUsedContractAuthority = null;
		}
		if (this.lastUsedContractAuthority) {
			this.contractNoticeFormGroup.controls['contractAuthorityId'].setValue(this.lastUsedContractAuthority);
		}

		this.contractNoticeFormGroup.enable();

		this.mode = 'NEW';
		setTimeout(() => {
			this.selectedTabIndex = 0;
		}, 1000);
	}

	displayFnMedium(item: ManagementMediumResponse): string {
		return item && item.id ? this.utils.getParameterByLanguageScript(item, 'name') : '';
	}

	displayFnBusinessEntity(item: ManagementBusinessEntityResponse): string {
		return item && item.id ? this.utils.getParameterByLanguageScript(item, 'name', true) : '';
	}

	displayFnContractNotice(item: ManagementContractNoticeResponse): string {
		return item && item.id ? this.utils.getParameterByLanguageScript(item, 'contractSubject', true) : '';
	}

	private openBusinessEntityManagementDialog() {
		const dialogRef = this.dialog.open(BusinessEntityComponent, {
			width: '600px',
			data: {
				mode: 'NEW',
				type: 'BUSINESS_ENTITY',
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				this.filteredBusinessEntities = [];
			});
	}

	private openMediumManagementDialog() {
		const dialogRef = this.dialog.open(MediumComponent, {
			width: '600px',
			data: {
				mode: 'NEW',
				type: 'MEDIUM',
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				this.filteredMediums = [];
			});
	}
	private autoPopulateMediumFromUrl(url: string) {
		try {
			const domain = new URL(url).hostname.replace('www.', '');
			this.apiContractNoticesMedium
				.searchMediums(domain)
				.pipe(take(1))
				.subscribe(response => {
					if (response && response.length > 0 && this.selectedMediums.length === 0) {
						this.selectedMediums.push(response[0]);
						this.contractNoticeFormGroup.controls['mediumId'].setValue(null);
						if (this.mediumInput) {
							this.mediumInput.nativeElement.value = '';
						}
					}
				});
		} catch (e) {
			// invalid URL, ignore
		}
	}
	/** (END)Form methods */

	//** File upload methods */
	getAcceptedFileFormats() {
		return ['application/pdf', 'image/*'];
	}

	selectFilesForUpload(event: any) {
		let type: 'NOTICE' | 'ANNOUNCEMENT' = 'ANNOUNCEMENT';
		if (
			this.dataAdministrationType === 'CONTRACT_NOTICES' &&
			this.contractNoticeFormGroup.controls['type'].value !== 'MODIFICATION' &&
			this.contractNoticeFormGroup.controls['type'].value !== 'CANCELLATION'
		) {
			type = 'NOTICE';
		}
		this.uploadManagement.generatePresignedUrl(event.target.files[0], type, 'CONTRACT_NOTICES');
		this.uploadManagement.linkGenerated.pipe(take(1)).subscribe(response => {
			this.contractNoticeFormGroup.controls['fileUrl'].setValue(response);
		});
	}
	//** (END)File upload methods */

	itemAction(
		action: 'REMOVE' | 'ADD' | 'SELECT' | 'SEARCH' | 'CREATE',
		type: 'MEDIUM' | 'BUSINESS_ENTITY' | 'CONTRACT_NOTICE',
		data: string | ManagementMediumResponse | ManagementContractNoticeResponse | MatChipInputEvent | MatAutocompleteSelectedEvent
	) {
		switch (type) {
			case 'BUSINESS_ENTITY': {
				if (action === 'SEARCH') {
					if (data && (data as string).length >= 1) {
						this.apiManagementBusinessEntity
							.getBusinessEntitiesPageable({ businessEntity: data as string }, 0, 100)
							.pipe(take(1))
							.subscribe({
								next: response => {
									this.filteredBusinessEntities = response.content || [];
								},
							});
					}
				} else if (action === 'CREATE') {
					this.openBusinessEntityManagementDialog();
				} else if (action === 'SELECT') {
					this.selectedContractors.push((data as MatAutocompleteSelectedEvent).option.value);
					this.contractNoticeFormGroup.controls['contractorIds'].setValue(null);
					this.contractorInput.nativeElement.value = '';
				} else if (action === 'REMOVE') {
					this.selectedContractors = this.selectedContractors.filter(sc => sc.id !== (data as ManagementBusinessEntityResponse).id);
				}
				break;
			}
			case 'MEDIUM': {
				if (action === 'SEARCH') {
					if (data && (data as string).length >= 1) {
						this.apiContractNoticesMedium
							.searchMediums(data as string)
							.pipe(take(1))
							.subscribe(response => {
								this.filteredMediums = response;
							});
					}
				} else if (action === 'CREATE') {
					this.openMediumManagementDialog();
				} else if (action === 'REMOVE') {
					this.selectedMediums = this.selectedMediums.filter(medium => medium.id !== (data as ManagementMediumResponse).id);
				} else if (action === 'SELECT') {
					this.selectedMediums.push((data as MatAutocompleteSelectedEvent).option.value);
					this.contractNoticeFormGroup.controls['mediumId'].setValue(null);
					this.mediumInput.nativeElement.value = '';
				}
				break;
			}
			case 'CONTRACT_NOTICE': {
				if (action === 'SEARCH') {
					this.filteredContractNotices = [];
					if (data && (data as string).length >= 1) {
						this.apiManagementContractNotice
							.searchContractNotices(data as string)
							.pipe(take(1))
							.subscribe({
								next: response => {
									this.filteredContractNotices = response || [];
								},
								error: () => {
									this.filteredContractNotices = [];
								}
							});
					}
				} else if (action === 'SELECT') {
					this.selectedContractNotice = (data as MatAutocompleteSelectedEvent).option.value;
					this.contractNoticeFormGroup.controls['contractNoticeId'].setValue(this.selectedContractNotice);
					this.contractNoticeInput.nativeElement.value = '';
					
					if (this.selectedContractNotice) {
						if (this.selectedContractNotice.number) {
							this.contractNoticeFormGroup.controls['number'].setValue(this.selectedContractNotice.number);
						}
						
						if (this.selectedContractNotice.contractAuthority) {
							this.contractNoticeFormGroup.controls['contractAuthorityId'].setValue(this.selectedContractNotice.contractAuthority);
						}
						
						if (this.selectedContractNotice.estimatedPurchaseValue) {
							this.contractNoticeFormGroup.controls['estimatedPurchaseValue'].setValue(this.selectedContractNotice.estimatedPurchaseValue);
						}
						
						if (this.selectedContractNotice.currency) {
							this.contractNoticeFormGroup.controls['currency'].setValue(this.selectedContractNotice.currency);
						}
						
						if (this.selectedContractNotice.mediums) {
							this.selectedMediums = [...this.selectedContractNotice.mediums];
						}
						
						if (this.selectedContractNotice.details && this.selectedContractNotice.details.length > 0) {
							this.selectedContractNotice.details.forEach(detail => {
								if (detail.lang && detail.contractSubject) {
									const langCode = detail.lang.toLowerCase();
									const controlName = 'contractSubject' + this.utils.titleCase(langCode);
									if (this.contractNoticeFormGroup.controls[controlName]) {
										this.contractNoticeFormGroup.controls[controlName].setValue(detail.contractSubject);
									}
								}
							});
						}
						
						if (this.selectedContractNotice.divisiblePurchase !== undefined) {
							this.contractNoticeFormGroup.controls['divisiblePurchase'].setValue(this.selectedContractNotice.divisiblePurchase);
						}
						
						if (this.selectedContractNotice.country) {
							this.contractNoticeFormGroup.controls['country'].setValue(this.selectedContractNotice.country);
						}
					}
				}
				break;
			}
		}
	}

	submitContractNoticeForm(formDirective: FormGroupDirective, event: Event) {
		event.stopPropagation();
		event.preventDefault();
		this.apiLoading.contractNotice = true;

		const contractNoticeDto: CreateContractNoticeRequest | UpdateContractNoticeRequest = {
			publishedDate: new Date(this.utils.setDateToDay(new Date(this.contractNoticeFormGroup.controls['publishedDate'].value), 'CURRENT_TIME')),
			deliveryDate: this.contractNoticeFormGroup.controls['deliveryDate'].value,
			type: this.contractNoticeFormGroup.controls['type'].value,
			url: this.contractNoticeFormGroup.controls['url'].value,
			mediumIds: this.selectedMediums.map(item => item.id),
			contractorIds: this.selectedContractors.map(item => item.id),
			number: this.contractNoticeFormGroup.controls['number'].value,
			contractAuthorityId: this.contractNoticeFormGroup.controls['contractAuthorityId'].value.id,
			country: this.contractNoticeFormGroup.controls['country'].value,
			details: this.avaliableLanguagesCodes
				.map(langCode => ({
					id: this.itemForEdit?.details?.find(detail => detail.lang === langCode.toUpperCase())?.id || crypto.randomUUID(),
					contractSubject: this.contractNoticeFormGroup.controls['contractSubject' + this.utils.titleCase(langCode)].value,
					lang: (<any>ContractNoticeDetailsCreateRequest.LangEnum)[langCode.toUpperCase()],
					translated: false,
				}))
				.filter(details => details.contractSubject),
			procurementType: this.contractNoticeFormGroup.controls['procurementType'].value,
			divisiblePurchase: this.contractNoticeFormGroup.controls['divisiblePurchase'].value,
			estimatedPurchaseValue: this.contractNoticeFormGroup.controls['estimatedPurchaseValue'].value,
			currency: this.contractNoticeFormGroup.controls['currency'].value,
			description: this.contractNoticeFormGroup.controls['description'].value,
			fileUrl: this.contractNoticeFormGroup.controls['fileUrl'].value,
			tagIds: this.contractNoticeFormGroup.controls['tagIds'].value,
		};

		if (this.selectedContractNotice && this.selectedContractNotice.id) {
			(contractNoticeDto as CreateContractAnnouncementRequest).contractNoticeId = this.selectedContractNotice.id;
		}

		if (this.mode === 'NEW') {
			if (
				this.dataAdministrationType === 'CONTRACT_NOTICES' &&
				this.contractNoticeFormGroup.controls['type'].value !== 'MODIFICATION' &&
				this.contractNoticeFormGroup.controls['type'].value !== 'CANCELLATION'
			) {
				this.createContractNotice(contractNoticeDto, formDirective);
			} else {
				this.createContractAnnouncement(contractNoticeDto, formDirective);
			}
		} else if (this.mode === 'EDIT') {
			if (
				this.dataAdministrationType === 'CONTRACT_NOTICES' &&
				this.contractNoticeFormGroup.controls['type'].value !== 'MODIFICATION' &&
				this.contractNoticeFormGroup.controls['type'].value !== 'CANCELLATION'
			) {
				this.updateContractNotice(contractNoticeDto, formDirective);
			} else {
				const updateAnnouncementRequest: UpdateContractAnnouncementRequest = {
					publishedDate: contractNoticeDto.publishedDate,
					deliveryDate: contractNoticeDto.deliveryDate,
					type: this.contractNoticeFormGroup.controls['type'].value,
					url: contractNoticeDto.url,
					country: contractNoticeDto.country,
					divisiblePurchase: contractNoticeDto.divisiblePurchase,
					currency: contractNoticeDto.currency,
					fileUrl: contractNoticeDto.fileUrl,
					description: contractNoticeDto.description,
					agreementValue: this.contractNoticeFormGroup.controls['agreementValue'].value,
					contractorIds: this.selectedContractors.map(item => item.id),
					tagIds: this.contractNoticeFormGroup.controls['tagIds'].value,
				};
				this.updateContractAnnouncement(updateAnnouncementRequest, formDirective);
			}
		}
	}

	private createContractNotice(contractNoticeDto: CreateContractNoticeRequest, formDirective: FormGroupDirective) {
		this.apiManagementContractNotice
			.createContractNotice(contractNoticeDto)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});

					if (contractNoticeDto.fileUrl) {
						this.uploadManagement.uploadFile(response);
					}

					this.lastUsedContractAuthority = this.contractNoticeFormGroup.controls['contractAuthorityId'].value;
					this.resetForm(formDirective);
					this.apiLoading.contractNotice = false;
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.contractNotice = false;
				},
			});
	}

	private updateContractNotice(contractNoticeDto: CreateContractNoticeRequest, formDirective: FormGroupDirective) {
		if (this.itemForEdit) {
			this.apiManagementContractNotice
				.updateContractNotice(contractNoticeDto as UpdateContractNoticeRequest, this.itemForEdit.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
							type: 'SUCCESS',
						});

						if (this.itemForEdit && !this.utils.compareSuppressNulls(this.itemForEdit.fileUrl, contractNoticeDto.fileUrl)) {
							this.uploadManagement.uploadFile(this.itemForEdit as ManagementContractNoticeResponse);
						}

						this.resetForm(formDirective);
						this.actionRequested.emit({ action: 'FILTER' });
						this.apiLoading.contractNotice = false;
						this.mode = 'NEW';
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.contractNotice = false;
					},
				});
		}
	}

	private createContractAnnouncement(contractNoticeDto: CreateContractNoticeRequest, formDirective: FormGroupDirective) {
		if (this.selectedContractNotice && this.selectedContractNotice.id) {
			const contractAnnouncemetRequest: CreateContractAnnouncementRequest = {
				publishedDate: contractNoticeDto.publishedDate,
				deliveryDate: contractNoticeDto.deliveryDate,
				type: this.contractNoticeFormGroup.controls['type'].value,
				url: contractNoticeDto.url,
				country: contractNoticeDto.country,
				divisiblePurchase: contractNoticeDto.divisiblePurchase,
				currency: contractNoticeDto.currency,
				contractNoticeId: this.selectedContractNotice.id,
				fileUrl: contractNoticeDto.fileUrl,
				description: contractNoticeDto.description,
				agreementValue: this.contractNoticeFormGroup.controls['agreementValue'].value,
				contractorIds: this.selectedContractors.map(item => item.id),
				tagIds: this.contractNoticeFormGroup.controls['tagIds'].value,
			};

			this.apiManagementContractAnnouncement
				.createContractAnnouncement(contractAnnouncemetRequest)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
							type: 'SUCCESS',
						});

						if (contractNoticeDto.fileUrl) {
							this.uploadManagement.uploadFile(response);
						}

						this.lastUsedContractAuthority = this.contractNoticeFormGroup.controls['contractAuthorityId'].value;
						this.resetForm(formDirective);
						this.apiLoading.contractNotice = false;
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.contractNotice = false;
					},
				});
		} else {
			contractNoticeDto.createdFromAnnouncement = true;
			contractNoticeDto.type = 'CONTRACT_NOTICE';

			this.apiManagementContractNotice
				.createContractNotice(contractNoticeDto)
				.pipe(take(1))
				.subscribe({
					next: response => {
						const contractAnnouncemetRequest: CreateContractAnnouncementRequest = {
							publishedDate: contractNoticeDto.publishedDate,
							deliveryDate: contractNoticeDto.deliveryDate,
							type: this.contractNoticeFormGroup.controls['type'].value,
							url: contractNoticeDto.url,
							country: contractNoticeDto.country,
							divisiblePurchase: contractNoticeDto.divisiblePurchase,
							currency: contractNoticeDto.currency,
							contractNoticeId: response.id,
							fileUrl: contractNoticeDto.fileUrl,
							description: contractNoticeDto.description,
							agreementValue: this.contractNoticeFormGroup.controls['agreementValue'].value,
							contractorIds: this.selectedContractors.map(item => item.id),
							tagIds: this.contractNoticeFormGroup.controls['tagIds'].value,
						};

						this.apiManagementContractAnnouncement
							.createContractAnnouncement(contractAnnouncemetRequest)
							.pipe(take(1))
							.subscribe({
								next: () => {
									this.sharedService.showNotification.next({
										message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
										type: 'SUCCESS',
									});

									if (contractNoticeDto.fileUrl) {
										this.uploadManagement.uploadFile(response);
									}

									this.lastUsedContractAuthority = this.contractNoticeFormGroup.controls['contractAuthorityId'].value;
									this.resetForm(formDirective);
									this.apiLoading.contractNotice = false;
								},
								error: err => {
									this.sharedService.showNotification.next({
										message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
										type: 'ERROR',
									});
									this.apiLoading.contractNotice = false;
								},
							});
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.contractNotice = false;
					},
				});
		}
	}

	private updateContractAnnouncement(contractAnnouncemetRequest: UpdateContractAnnouncementRequest, formDirective: FormGroupDirective) {
		if (this.itemForEdit) {
			this.apiManagementContractAnnouncement
				.updateContractAnnouncement(contractAnnouncemetRequest, this.itemForEdit.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
							type: 'SUCCESS',
						});

						if (this.itemForEdit && !this.utils.compareSuppressNulls(this.itemForEdit.fileUrl, contractAnnouncemetRequest.fileUrl)) {
							this.uploadManagement.uploadFile(this.itemForEdit as ManagementContractAnnouncementResponse);
						}

						this.resetForm(formDirective);
						this.actionRequested.emit({ action: 'FILTER' });
						this.apiLoading.contractNotice = false;
						this.mode = 'NEW';
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.contractNotice = false;
					},
				});
		}
	}

	openDeveloperListMissing() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				fields: [
					{
						type: 'date-range',
						data: [
							{
								name: '',
								value: [new Date(), new Date()],
							},
						],
					},
				],
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				const today = new Date();
				const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
				const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
				
				const fromDate = result.data.dateFrom ? new Date(result.data.dateFrom).getTime() : startOfDay.getTime();
				const toDate = result.data.dateTo ? new Date(result.data.dateTo).getTime() : endOfDay.getTime();

				if (fromDate && toDate) {
					this.apiManagementContractNotice
						.findMissingNotices(fromDate, toDate)
						.pipe(take(1))
						.subscribe({
							next: response => {
                this.dialog.open(ConfirmationDialogComponent, {
                  width: '600px',
                  data: {
                    mode: 'USER_ACTION',
                    type: 'message',
                    message: response.numbers?.length === 0 ? this.translate.instant('ASSORTED.NO_DATA_FOUND') : response.numbers?.join(', '),
                  },
                });
              },
						});
				}
			});
	}

	openAutoTagDialog() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				fields: [
					{
						type: 'date-range',
						data: [
							{
								name: '',
								value: [new Date(), new Date()],
							},
						],
					},
				],
			},
		});
		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result && result.data) {
					const today = new Date();
					const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
					const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
					
					const fromDate = result.data.dateFrom ? new Date(result.data.dateFrom) : startOfDay;
					const toDate = result.data.dateTo ? new Date(result.data.dateTo) : endOfDay;
					if (fromDate && toDate) {
						this.apiLoading.contractNotice = true;
						this.apiManagementContractNotice
							.autoTagContractNotices({
								dateFrom: fromDate,
								dateTo: toDate
							})
							.pipe(take(1))
							.subscribe({
								next: response => {
									this.sharedService.showNotification.next({
										message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
										type: 'SUCCESS',
									});
									this.actionRequested.emit({ action: 'FILTER' });
									this.apiLoading.contractNotice = false;
								},
								error: err => {
									this.sharedService.showNotification.next({
										message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
										type: 'ERROR',
									});
									this.apiLoading.contractNotice = false;
								},
							});
					}
				}
			});
	}

	showTranslateContractNoticesConfirmation() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '500px',
			data: {
				mode: 'USER_ACTION',
				type: 'message',
				message: this.translate.instant('NOTIFICATIONS.TRANSLATE_CONFIRMATION')
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					this.translateContractNotices();
				}
			});
	}

	private translateContractNotices() {
		this.apiLoading.translateContractNotices = true;

		this.apiManagementContractNotice.translateNonTranslatedContractNotices()
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_COMPLETE'),
						type: 'SUCCESS',
					});
					this.apiLoading.translateContractNotices = false;
				},
				error: (err) => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error?.message || err.message}`,
						type: 'ERROR',
					});
					this.apiLoading.translateContractNotices = false;
				},
			});
	}

	manuallySaveEnabavki(defaultTab?: number, defaultMacedonianType?: string, defaultKosovoType?: string) {
		// Prepare Macedonian selection types
		let macedonianSelectionType = [];
		if (this.dataAdministrationType === 'CONTRACT_NOTICES') {
			macedonianSelectionType = Constants.CONTRACT_NOTICE_TYPES.map(item => ({
				name: this.translate.instant('CONTRACT_NOTICES.' + item),
				value: item,
			}));
			macedonianSelectionType.push({
				name: `${this.translate.instant('CONTRACT_NOTICES.CONTRACT_NOTICE')} (${this.translate.instant('ASSORTED.GRID')})`,
				value: 'CONTRACT_NOTICE_GRID',
			});
			macedonianSelectionType.push({
				name: `${this.translate.instant('CONTRACT_NOTICES.MODIFICATION')} (${this.translate.instant('ASSORTED.GRID')})`,
				value: 'MODIFICATION_GRID',
			});
			macedonianSelectionType.push({
				name: `${this.translate.instant('CONTRACT_NOTICES.CANCELLATION')} (${this.translate.instant('ASSORTED.GRID')})`,
				value: 'CANCELLATION_GRID',
			});
			macedonianSelectionType.push({
				name: `${this.translate.instant('CONTRACT_NOTICES.E_MARKET')} (${this.translate.instant('ASSORTED.GRID')})`,
				value: 'E_MARKET_GRID',
			});
		} else {
			macedonianSelectionType = Constants.CONTRACT_ANNOUNCEMENT_TYPES.map(item => ({
				name: this.translate.instant('CONTRACT_NOTICES.' + item),
				value: item,
			}));
			macedonianSelectionType.push({
				name: `${this.translate.instant('CONTRACT_NOTICES.CONTRACT_AWARD')} (${this.translate.instant('ASSORTED.GRID')})`,
				value: 'CONTRACT_AWARD_GRID',
			});
		}

		// Prepare Kosovo selection types
		const kosovoSelectionType = [
			{
				name: this.translate.instant('CONTRACT_NOTICES.CONTRACT_NOTICE'),
				value: 'CONTRACT_NOTICE',
			},
			// {
			// 	name: `${this.translate.instant('CONTRACT_NOTICES.CONTRACT_NOTICE')} (${this.translate.instant('ASSORTED.LIST')})`,
			// 	value: 'CONTRACT_NOTICE_LIST',
			// },
			{
				name: this.translate.instant('CONTRACT_NOTICES.TECHNICAL_DIALOGUES'),
				value: 'TECHNICAL_DIALOGUES',
			},
			// {
			// 	name: `${this.translate.instant('CONTRACT_NOTICES.TECHNICAL_DIALOGUES')} (${this.translate.instant('ASSORTED.LIST')})`,
			// 	value: 'TECHNICAL_DIALOGUES_LIST',
			// },
		];

		const macedonianFields: any[] = [
			{
				type: 'select',
				label: this.translate.instant('CONTRACT_NOTICES.TYPE'),
				key: 'macedonianType',
				options: macedonianSelectionType.map(item => item.value),
				optionLabels: macedonianSelectionType.map(item => item.name),
				value: defaultMacedonianType,
			},
			{
				type: 'textarea',
				key: 'macedonianBody',
			},
		];

		if (this.dataAdministrationType !== 'CONTRACT_NOTICES') {
			macedonianFields.push({
				type: 'radio',
				key: 'emarketSource',
				options: ['ENABAVKI', 'EMARKET'],
				optionLabels: [
					this.translate.instant('CONTRACT_NOTICES.E_NABAVKI'),
					this.translate.instant('CONTRACT_NOTICES.E_PAZAR'),
				],
				value: 'ENABAVKI',
				showWhen: { key: 'macedonianType', values: ['LATEST_DECISION', 'CONTRACT_AWARD_GRID'] },
			});
		}

		const tabs = [
			{
				label: this.translate.instant('COUNTRIES.MACEDONIA'),
				fields: macedonianFields,
			},
		];

		if (this.dataAdministrationType === 'CONTRACT_NOTICES') {
			tabs.push({
				label: this.translate.instant('COUNTRIES.KOSOVO'),
				fields: [
					{
						type: 'select',
						label: this.translate.instant('CONTRACT_NOTICES.TYPE'),
						key: 'kosovoType',
						options: kosovoSelectionType.map(item => item.value),
						optionLabels: kosovoSelectionType.map(item => item.name),
						value: defaultKosovoType,
					},
					{
						type: 'textarea',
						key: 'kosovoBody',
					},
				],
			});
		}

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '800px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-tabs',
				tabs,
				defaultTab: defaultTab,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result && result.data) {
					const tabIndex = result.activeTab;

					// Handle Macedonian (tab 0)
					if (tabIndex === 0 && result.data.macedonianType && result.data.macedonianBody) {
						const validJson = result.data.macedonianBody
							.replace(/\n/g, '')
							.replace(/\s{2,}/g, ' ')
							.trim();

						this.apiLoading.contractNotice = true;

						let selection = result.data.macedonianType;
						let isGrid = false;
						if (selection.includes('GRID')) {
							selection = selection.replaceAll('_GRID', '');
							isGrid = true;
						}

						const isAnnouncement = this.dataAdministrationType !== 'CONTRACT_NOTICES';
						const isEmarketEligible =
							result.data.macedonianType === 'LATEST_DECISION' || result.data.macedonianType === 'CONTRACT_AWARD_GRID';
						const isEmarket = isAnnouncement && isEmarketEligible && result.data.emarketSource === 'EMARKET';

						const request: ENabavkiBodyRequest = {
							type: selection,
							body: validJson,
							grid: isGrid,
							eMarket: isEmarket,
						};

						if (this.dataAdministrationType === 'CONTRACT_NOTICES' && selection !== 'MODIFICATION' && selection !== 'CANCELLATION') {
							this.apiManagementContractNotice
								.manuallySaveENabavkiNoticeOfType(request)
								.pipe(take(1))
								.subscribe({
									next: () => {
										this.sharedService.showNotification.next({
											message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
											type: 'SUCCESS',
										});
										this.actionRequested.emit({ action: 'FILTER' });
										this.apiLoading.contractNotice = false;
										this.manuallySaveEnabavki(tabIndex, result.data.macedonianType, undefined);
									},
									error: err => {
										this.sharedService.showNotification.next({
											message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
											type: 'ERROR',
										});
										this.apiLoading.contractNotice = false;
									},
								});
						} else {
							this.apiManagementContractAnnouncement
								.manuallySaveENabavkiAnnouncementOfType(request)
								.pipe(take(1))
								.subscribe({
									next: () => {
										this.sharedService.showNotification.next({
											message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
											type: 'SUCCESS',
										});
										this.actionRequested.emit({ action: 'FILTER' });
										this.apiLoading.contractNotice = false;
										this.manuallySaveEnabavki(tabIndex, result.data.macedonianType, undefined);
									},
									error: err => {
										this.sharedService.showNotification.next({
											message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
											type: 'ERROR',
										});
										this.apiLoading.contractNotice = false;
									},
								});
						}
					}

					// Handle Kosovo (tab 1)
					else if (tabIndex === 1 && result.data.kosovoType && result.data.kosovoBody) {
						const htmlBody = result.data.kosovoBody.trim();

						this.apiLoading.contractNotice = true;

						let selection = result.data.kosovoType;
						let isList = false;
						if (selection.includes('_LIST')) {
							selection = selection.replaceAll('_LIST', '');
							isList = true;
						}

						const request: EProkurimiBodyRequest = {
							type: selection,
							body: htmlBody,
							list: isList,
						};

						this.apiManagementContractNotice
							.manuallySaveEProkurimiNoticeOfType(request)
							.pipe(take(1))
							.subscribe({
								next: () => {
									this.sharedService.showNotification.next({
										message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
										type: 'SUCCESS',
									});
									this.actionRequested.emit({ action: 'FILTER' });
									this.apiLoading.contractNotice = false;
									this.manuallySaveEnabavki(tabIndex, undefined, result.data.kosovoType);
								},
								error: err => {
									this.sharedService.showNotification.next({
										message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
										type: 'ERROR',
									});
									this.apiLoading.contractNotice = false;
								},
							});
					}
				}
			});
	}

	instantProcessNotices() {
		let macedonianTypes = [];
		if (this.dataAdministrationType === 'CONTRACT_NOTICES') {
			macedonianTypes = Constants.CONTRACT_NOTICE_TYPES.map(item => ({
				name: this.translate.instant('CONTRACT_NOTICES.' + item),
				value: item,
			}));
		} else {
			macedonianTypes = Constants.CONTRACT_ANNOUNCEMENT_TYPES.map(item => ({
				name: this.translate.instant('CONTRACT_NOTICES.' + item),
				value: item,
			}));
		}

		let kosovoTypes = [];
		if (this.dataAdministrationType === 'CONTRACT_NOTICES') {
			const kosovoNoticeTypes = ['CONTRACT_NOTICE', 'TECHNICAL_DIALOGUES', 'MODIFICATION', 'CANCELLATION'];
			kosovoTypes = kosovoNoticeTypes.map(item => ({
				name: this.translate.instant('CONTRACT_NOTICES.' + item),
				value: item,
			}));
		} else {
			const kosovoAnnouncementTypes = ['CONTRACT_AWARD', 'LATEST_DECISION'];
			kosovoTypes = kosovoAnnouncementTypes.map(item => ({
				name: this.translate.instant('CONTRACT_NOTICES.' + item),
				value: item,
			}));
		}

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				fields: [
					{
						type: 'radio',
						label: this.translate.instant('COUNTRIES.COUNTRY'),
						key: 'country',
						options: ['mk', 'xk'],
						optionLabels: [
							this.translate.instant('COUNTRIES.MACEDONIA'),
							this.translate.instant('COUNTRIES.KOSOVO')
						],
						defaultValue: 'mk',
					},
					{
						type: 'selection',
						label: this.translate.instant('CONTRACT_NOTICES.TYPE'),
						key: 'noticeType',
						data: macedonianTypes,
						kosovoData: kosovoTypes,
					},
					{
						type: 'textfield',
						label: 'User Agent',
						key: 'textfield',
						countrySpecific: 'mk',
					},
					{
						type: 'textfield',
						label: this.translate.instant('CONTRACT_NOTICES.DAYS_BEHIND'),
						key: 'daysBehind',
						countrySpecific: 'xk',
					},
				],
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result && result.data && result.data.noticeType) {
					const selectedCountry = result.data.country || 'mk';
					const type = result.data.noticeType;
					const customUserAgent = result.data.textfield || undefined;
					const daysBehind = result.data.daysBehind ? Number(result.data.daysBehind) : undefined;

					if (selectedCountry === 'xk') {
						this.processEProkurimi(type, daysBehind);
					} else {
						this.processENabavki(type, customUserAgent);
					}
				}
			});
	}

	private processENabavki(type: string, customUserAgent?: string) {
		const request: ManualSchedulingRequest = {
			type: type as any,
			customUserAgent: customUserAgent,
		};

		this.apiLoading.contractNotice = true;
		this.apiManagementManualScheduling
			.instantlyProcessENabavkiNoticesAndAnnouncements(request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTIVATED'),
						type: 'SUCCESS',
					});
					this.apiLoading.contractNotice = false;
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.contractNotice = false;
				},
			});
	}

	private processEProkurimi(type: string, daysBehind?: number) {
		const request: ManualSchedulingRequest = {
			type: type as any,
			daysBehind: daysBehind,
		};

		this.apiLoading.contractNotice = true;
		this.apiManagementManualScheduling
			.instantlyProcessEProkurimiNoticesAndAnnouncements(request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTIVATED'),
						type: 'SUCCESS',
					});
					this.apiLoading.contractNotice = false;
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.contractNotice = false;
				},
			});
	}

	openScreenshotQueue() {
		this.dialog.open(ScreenshotQueueDialogComponent, {
			width: '700px',
			data: {
				apiService: this.apiScreenshot,
				showContractType: true,
			},
		});
	}
}
